import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const DatePicker = ({ initialRange, onRangeChange }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [startDate, setStartDate] = useState(initialRange?.startDate || null);
  const [endDate, setEndDate] = useState(initialRange?.endDate || null);
  const [hoverDate, setHoverDate] = useState(null);

  // Update local state when initialRange changes
  useEffect(() => {
    if (initialRange) {
      setStartDate(initialRange.startDate);
      setEndDate(initialRange.endDate);
    }
  }, [initialRange]);

  const daysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const firstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const isInRange = (date) => {
    if (!startDate || !endDate) return false;
    return date > startDate && date < endDate;
  };

  const isInHoverRange = (date) => {
    if (!startDate || !hoverDate || endDate) return false;
    return (date > startDate && date <= hoverDate) || (date < startDate && date >= hoverDate);
  };

  const handleDateSelect = (day) => {
    const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    
    if (!startDate || (startDate && endDate)) {
      setStartDate(selectedDate);
      setEndDate(null);
    } else {
      if (selectedDate < startDate) {
        setEndDate(startDate);
        setStartDate(selectedDate);
      } else {
        setEndDate(selectedDate);
      }
      
      // Only call the onRangeChange callback when both dates are selected
      if (onRangeChange && typeof onRangeChange === 'function') {
        // Use setTimeout to ensure state updates have processed
        setTimeout(() => {
          const newStart = selectedDate < startDate ? selectedDate : startDate;
          const newEnd = selectedDate < startDate ? startDate : selectedDate;
          onRangeChange({ startDate: newStart, endDate: newEnd });
        }, 0);
      }
    }
  };

  const handleMouseEnter = (day) => {
    if (startDate && !endDate) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      setHoverDate(date);
    }
  };

  const renderCalendarDays = () => {
    const days = [];
    const daysCount = daysInMonth(currentMonth);
    const firstDay = firstDayOfMonth(currentMonth);
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8 w-8"></div>);
    }
    
    // Add cells for each day in the month
    for (let day = 1; day <= daysCount; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isStart = isSameDay(date, startDate);
      const isEnd = isSameDay(date, endDate);
      const isSelected = isStart || isEnd;
      const inRange = isInRange(date);
      const inHoverRange = isInHoverRange(date);
      
      days.push(
        <div 
          key={day}
          onClick={() => handleDateSelect(day)}
          onMouseEnter={() => handleMouseEnter(day)}
          className={`
            h-8 w-8 flex items-center justify-center rounded-full cursor-pointer text-sm
            ${isSelected ? 'bg-primary text-white' : ''}
            ${(inRange || inHoverRange) && !isSelected ? 'bg-primary/20 text-primary' : ''}
            ${!isSelected && !inRange && !inHoverRange ? 'hover:bg-gray-100' : ''}
          `}
        >
          {day}
        </div>
      );
    }
    
    return days;
  };

  return (
    <div className="select-none">
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={prevMonth}
          className="p-1 rounded-full hover:bg-gray-100"
        >
          <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
        </button>
        <div className="font-medium capitalize">
          {formatMonthYear(currentMonth)}
        </div>
        <button
          onClick={nextMonth}
          className="p-1 rounded-full hover:bg-gray-100"
        >
          <ChevronRightIcon className="h-5 w-5 text-gray-600" />
        </button>
      </div>
      
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, index) => (
          <div 
            key={`header-${day}-${index}`}
            className="h-8 w-8 flex items-center justify-center text-xs text-gray-500 font-medium"
          >
            {day}
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {renderCalendarDays()}
      </div>
      
      <div className="mt-4 text-center text-sm text-gray-600">
        {startDate && !endDate ? 'Sélectionnez une date de fin' : 'Sélectionnez une plage de dates'}
      </div>
      
      {startDate && endDate && (
        <div className="mt-4 pt-4 border-t flex justify-center">
          <button
            onClick={() => {
              if (onRangeChange) {
                onRangeChange({ startDate, endDate });
              }
            }}
            className="px-4 py-2 bg-primary text-white rounded text-sm hover:bg-primary/90"
          >
            Appliquer
          </button>
        </div>
      )}
    </div>
  );
};

export default DatePicker;